/* eslint-disable no-console */
import * as Sentry from '@sentry/react';

const isSentryEnabled = process.env.SENTRY_ENABLED === '1';
const env = process.env.APP_ENV || 'local';

if (isSentryEnabled) {
  Sentry.init({
    environment: env,
    dsn: process.env.SENTRY_DSN,
    release: process.env.SENTRY_RELEASE,
    integrations: [
      Sentry.extraErrorDataIntegration({
        depth: 5,
      }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [process.env.SENTRY_APP_KEY!],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    ],
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // normalizeDepth needed for ExtraErrorData.depth to work.
    // It should be depth + 1. More details:
    // https://github.com/getsentry/sentry-javascript/issues/1964#issuecomment-625353135
    normalizeDepth: 6,
    tracesSampleRate: 0,
  });
}

interface LogContext {
  [key: string]: any;
  error?: unknown;
  message?: never;
}

export class Logger {
  info(message: string, context: LogContext = {}) {
    console.log(message, context);
  }

  error(message: string, context: LogContext = {}) {
    console.error(message, context);

    sendLog({
      level: 'error',
      message,
      context,
    });
  }

  warn(message: string, context: LogContext = {}) {
    console.warn(message, context);

    sendLog({
      level: 'warn',
      message,
      context,
    });
  }

  setUser(user: Sentry.User | null) {
    if (!isSentryEnabled) {
      return;
    }

    Sentry.setUser(user);
  }

  setExtras(extras: { [key: string]: any }) {
    if (!isSentryEnabled) {
      return;
    }

    Sentry.setExtras(extras);
  }
}

function sendLog({
  level,
  message,
  context = {},
}: {
  level: 'error' | 'warn';
  message: string;
  context: LogContext;
}) {
  if (!isSentryEnabled) {
    return;
  }

  let error: Error | undefined;
  let errorContext: { [key: string]: any } = {};
  let fingerprint: string[] | undefined;

  const { error: sourceError, ...rest } = context;

  if (sourceError instanceof Error) {
    if (sourceError.message === message) {
      error = sourceError;
    } else {
      // if this is a log with custom message, e.g. logger.warn('foo', { error })
      // lets collect another stack trace to show in sentry both the source of
      // the logger call and the source of Error object
      // this also will force sentry to use the `message` as the error description
      // instead of `sourceError.message`
      error = Object.assign(new Error(message), {
        cause: sourceError,
      });

      if (typeof Error.captureStackTrace === 'function') {
        Error.captureStackTrace(error, Logger.prototype[level]);
      }
    }

    const { name: _1, stack: _2, message: _3, ...extraProps } = sourceError;
    errorContext = extraProps;
  }

  if (!error) {
    // if we have error instance, let sentry to group events as it wants
    // otherwise let's control grouping based on log params
    fingerprint = [getMessageFingerprint(message), level, env];
  }

  Sentry.captureMessage(
    // @ts-expect-error sentry accepts error in place of message in captureMessage
    error || message,
    {
      level,
      extra: {
        ...rest,
        ...(Object.keys(errorContext).length ? { errorContext } : {}),
      },
      fingerprint,
    },
  );
}

function getMessageFingerprint(message: string): string {
  // try to filter out json and ids from messages
  const match = message.match(/^([A-Z\s,.:]+)(\s.*|$)/i);

  return match ? match[1] : 'undefined';
}

export const logger = new Logger();
